import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { CmsBlock, CmsDocumentSummary } from "@/api/cms";

/**
 * Renders CMS blocks with the app's own components. Content is data, so this
 * is the only place that knows how a block becomes markup.
 *
 * Inline syntax is deliberately tiny — `**bold**`, `` `code` `` and
 * `[label](/path)` — enough for the docs we have without pulling in a
 * markdown parser. Internal paths become router links so navigation stays
 * client-side; absolute URLs open in a new tab.
 */

const INLINE = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`)/g;

export function renderInline(text: string): ReactNode {
  const parts = text.split(INLINE).filter((p) => p !== "");
  return parts.map((part, i) => {
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link) {
      const [, label, href] = link;
      if (href.startsWith("/")) {
        return (
          <Link key={i} to={href} className="text-brand underline">
            {label}
          </Link>
        );
      }
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noreferrer"
          className="text-brand underline"
        >
          {label}
        </a>
      );
    }
    const bold = /^\*\*([^*]+)\*\*$/.exec(part);
    if (bold) {
      return (
        <strong key={i} className="text-foreground">
          {bold[1]}
        </strong>
      );
    }
    const code = /^`([^`]+)`$/.exec(part);
    if (code) {
      return (
        <code key={i} className="text-foreground">
          {code[1]}
        </code>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

export function CmsBlocks({
  blocks,
  index,
  basePath,
}: {
  blocks: CmsBlock[];
  index: CmsDocumentSummary[];
  basePath: string;
}) {
  return (
    <div className="space-y-5 text-sm text-foreground">
      {blocks.map((block, i) => (
        <CmsBlockView key={i} block={block} index={index} basePath={basePath} />
      ))}
    </div>
  );
}

function CmsBlockView({
  block,
  index,
  basePath,
}: {
  block: CmsBlock;
  index: CmsDocumentSummary[];
  basePath: string;
}) {
  switch (block.type) {
    case "p":
      return (
        <p className="leading-relaxed text-muted-foreground">
          {renderInline(block.text)}
        </p>
      );

    case "h2":
      return (
        <h2 className="pt-2 text-base font-semibold text-foreground">
          {renderInline(block.text)}
        </h2>
      );

    case "code":
      return (
        <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-border bg-bg px-3 py-2 font-mono text-xs text-foreground">
          {block.text}
        </pre>
      );

    case "ul":
      return (
        <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
          {block.items.map((item, i) => (
            <li key={i} className="leading-relaxed">
              {renderInline(item)}
            </li>
          ))}
        </ul>
      );

    case "ol":
      return (
        <ol className="list-decimal space-y-2 pl-5 text-muted-foreground">
          {block.items.map((item, i) => (
            <li key={i} className="leading-relaxed">
              {renderInline(item)}
            </li>
          ))}
        </ol>
      );

    case "table":
      return (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-surface/50 text-xs uppercase tracking-wide text-muted-foreground">
                {block.headers.map((h) => (
                  <th key={h} className="px-3 py-2 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} className="border-b border-border/60 last:border-0">
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className="px-3 py-2 align-top text-muted-foreground"
                    >
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case "callout":
      return (
        <section className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-3">
            <h3 className="text-sm font-semibold text-foreground">
              {block.title}
            </h3>
          </div>
          <div className="px-4 py-3 text-sm text-muted-foreground">
            {block.text && (
              <p className="leading-relaxed">{renderInline(block.text)}</p>
            )}
            {block.items && (
              <ul className="list-disc space-y-2 pl-5">
                {block.items.map((item, i) => (
                  <li key={i} className="leading-relaxed">
                    {renderInline(item)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      );

    case "docIndex":
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          {index
            .filter((doc) => doc.slug !== "")
            .map((doc) => (
              <Link
                key={doc.documentId}
                to={`${basePath}/${doc.slug}`}
                className="rounded-lg border border-border bg-card p-4 transition hover:bg-muted/10"
              >
                <p className="text-sm font-semibold text-foreground">
                  {doc.label ?? doc.title}
                </p>
                {doc.blurb && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {doc.blurb}
                  </p>
                )}
                <p className="mt-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                  {doc.audience === "ops" ? "Ops / maintainers" : "Everyone"}
                </p>
              </Link>
            ))}
        </div>
      );

    default:
      return null;
  }
}
